import { Clickable, Icon } from 'components';
import { Api, capitalize, formatLocale, theme, titleToLocaleKey } from 'lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSubscriptionState from 'hooks/useSubscriptionState';
import { useNavigate } from 'react-router-dom';
import { getFormattedAmountWithInterval, getPaymentMethodDisplay, getStatusStyle } from './helpers';

const SubscriptionCard = ({ sub, isMobile, onOpenDetails }) => {
  const { t } = useTranslation();
  const style = getStatusStyle(sub.status);
  const isRefunded = String(sub.status || '').toLowerCase() === 'refunded';
  const subscriptionState = useSubscriptionState(sub);
  const navigate = useNavigate();
  const [isOpeningPaymentUpdate, setIsOpeningPaymentUpdate] = useState(false);

  async function handleUpdatePaymentDetails() {
    if (!sub?.subscriptionId || isOpeningPaymentUpdate) return;

    setIsOpeningPaymentUpdate(true);

    const res = await Api.post('/subscriptionPlans/updatePaymentDetails', {
      subscriptionId: sub.subscriptionId
    });

    const updateUrl = res?.data?.url;
    if (typeof updateUrl === 'string' && updateUrl) {
      window.location.href = updateUrl;
      return;
    }

    setIsOpeningPaymentUpdate(false);
    navigate('/billing?tab=0');
  }

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
      {/* Status Warning Banner */}
      {subscriptionState.blockReason && (
        <div
          className={`flex items-start gap-2 ${isMobile ? 'px-4 pt-4 pb-2' : 'px-6 pt-5 pb-3'} bg-amber-50 border-b border-amber-100`}
        >
          <Icon
            type="warning"
            col="#f59e0b"
            dimensions={16}
            style={{ flexShrink: 0, marginTop: '2px' }}
          />
          <div className="flex-1">
            <p className="text-xs font-medium text-amber-900 font-[CeraPro]">
              {subscriptionState.blockReason}
            </p>
            {subscriptionState.actionRequired === 'update_payment' && (
              <div className="mt-1.5">
                <p className="text-xs text-amber-700 font-[CeraPro]">
                  {t('billing.updatePaymentMethod')}
                </p>
                <button
                  onClick={handleUpdatePaymentDetails}
                  disabled={isOpeningPaymentUpdate}
                  className={`mt-2 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold text-white font-[CeraPro] transition-colors ${
                    isOpeningPaymentUpdate
                      ? 'bg-amber-400 cursor-not-allowed'
                      : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  {t('billing.updatePaymentDetails')}
                </button>
              </div>
            )}
            {subscriptionState.actionRequired === 'contact_support' && (
              <p className="text-xs text-amber-700 font-[CeraPro] mt-1">
                {t('billing.contactSupport')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Top row */}
      <div
        className={`flex items-center justify-between ${isMobile ? 'p-4 pb-3' : 'px-6 py-5 pb-4'}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 shrink-0">
            <Icon type="factCheck" col={theme.GREY_70} dimensions={18} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 font-[CeraPro]">{sub.teamName}</p>
            <p className="text-xs text-gray-500 font-[CeraPro]">
              {t(`subscription.plans.${titleToLocaleKey(sub.planTitle)}`, {
                defaultValue: sub.planTitle
              })}
            </p>
          </div>
        </div>

        <div className="flex gap-2.5">
          <div className="flex flex-col gap-1">
            <span
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text} ${style.ring}`}
            >
              <span
                className={`inline-block relative top-[0.5px] w-1.5 h-1.5 rounded-full ${style.dot}`}
              />
              {t(`billing.status_${sub.status}`, { defaultValue: capitalize(sub.status) })}
            </span>
            {!isRefunded && sub.refundRequestStatus === 'pending' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 font-[CeraPro]">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                {t('billing.refundPending', { defaultValue: 'Refund Requested — Pending Approval' })}
              </span>
            )}
            {!isRefunded && sub.refundRequestStatus === 'approved' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 font-[CeraPro]">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {t('billing.refundApproved', { defaultValue: 'Refund Approved' })}
              </span>
            )}
            {!isRefunded && sub.refundRequestStatus === 'rejected' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 font-[CeraPro]">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" />
                {t('billing.refundRejected', { defaultValue: 'Refund Rejected' })}
              </span>
            )}
            {sub.gracePeriod != null && (
              <p className="text-[10px] text-amber-600 font-medium font-[CeraPro]">
                {sub.gracePeriod === 0
                  ? t('billing.expiresToday')
                  : t('billing.expiresInDays', { count: sub.gracePeriod })}
              </p>
            )}
          </div>
          {!isMobile && (
            <p className="text-sm font-bold text-gray-900 font-[CeraPro]">
              {getFormattedAmountWithInterval(sub)}
            </p>
          )}
        </div>
      </div>

      {/* Detail row */}
      <div className={`border-t border-gray-100 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}>
        <div
          className={`${isMobile ? 'grid grid-cols-2 gap-y-3 gap-x-2' : 'flex items-center gap-8'}`}
        >
          {isMobile && (
            <div>
              <p className="text-xs text-gray-400 font-[CeraPro]">{t('billing.amount')}</p>
              <p className="text-sm font-bold text-gray-900 font-[CeraPro] mt-0.5">
                {getFormattedAmountWithInterval(sub)}
              </p>
            </div>
          )}

          <div>
            <p className="text-xs text-gray-400 font-[CeraPro]">{t('billing.cycle')}</p>
            <p className="text-sm text-gray-900 font-[CeraPro] mt-0.5">
              {capitalize(sub.billingCycle)}
            </p>
          </div>

          {sub.nextRenewalAt && (
            <div>
              <p className="text-xs text-gray-400 font-[CeraPro]">{t('billing.nextPayment')}</p>
              <p className="text-sm text-gray-900 font-[CeraPro] mt-0.5">
                {formatLocale(sub.nextRenewalAt, 'MMM d, yyyy')}
              </p>
            </div>
          )}

          <div>
            <p className="text-xs text-gray-400 font-[CeraPro]">{t('billing.payment')}</p>
            <p className="text-sm text-gray-900 font-[CeraPro] mt-0.5">
              {getPaymentMethodDisplay(sub)}
            </p>
          </div>

          {!isMobile && (
            <Clickable onClick={() => onOpenDetails(sub)} style={{ marginLeft: 'auto' }}>
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold text-gray-600 font-[CeraPro]">
                  {t('billing.details')}
                </span>
                <Icon type="arrowRight" dimensions={12} col={theme.GREY_60} />
              </div>
            </Clickable>
          )}
        </div>

        {isMobile && (
          <Clickable onClick={() => onOpenDetails(sub)} marg="12px 0 0">
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-gray-600 font-[CeraPro]">
                {t('billing.viewDetails')}
              </span>
              <Icon type="arrowRight" dimensions={12} col={theme.GREY_60} />
            </div>
          </Clickable>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
